import type { CustomStoreImplementationOptions, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { defineStore } from 'pinia'
import { initAliveStoreIds, kapixContext, openUrl, remove, scrollInfo } from 'kapix-components-vue3'
import formationsSeoStoreCustom from './store.custom'
import { $translate } from '~/modules/i18n'

const storeName = 'formationsSeo'
const customImplement: CustomStoreImplementationOptions = formationsSeoStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function formationsSeoFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        // Page info,
        $title: $translate('prog-01-seo.title', 'Formation SEO sur-mesure'),
        $description: $translate('prog-01-seo.description', 'Boostez votre référencement naturel grâce à notre formation sur-mesure en SEO. Coaché par un expert en SEO, vous maîtriserez les techniques et les bonnes pratiques pour améliorer votre visibilité sur les moteurs de recherche.'),
        $publishedTime: new Date('2023-02-13T07:42:17.618Z'),
        $modifiedTime: new Date('2023-09-06T12:38:27.671Z'),
        // Constants,
        $constants: {
          testProfilsExpertsTags: [
            { Nom: 'Google Search Console' },
            { Nom: 'SEO local' },
            { Nom: 'SEO technique' },
            { Nom: 'SEA' },
            { Nom: 'Google Analytics' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          blocPreviewProfilExpertTags: [
            { Nom: 'Marketing de contenu' },
            { Nom: 'Magento' },
            { Nom: 'Acquisition' },
            { Nom: 'Conversion' },
            { Nom: 'SEA' },
            { Nom: 'Google Adwords' },
            { Nom: 'Google Ads' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element1Tags: [
            { Nom: 'Netlinking' },
            { Nom: 'SEO international' },
            { Nom: 'Technical SEO' },
            { Nom: 'Google Analytics' },
            { Nom: 'Trafic management' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element2Tags: [
            { Nom: 'Netlinking' },
            { Nom: 'SEO international' },
            { Nom: 'Technical SEO' },
            { Nom: 'Google Analytics' },
            { Nom: 'Trafic management' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          dropDownFormationArguments: [
            { Text: '<ul><li>Appréhender le marché de la recherche en ligne.</li></ul>' },
            { Text: '<ul><li>Identifier les 3 piliers du référencement naturel : technique, éditorial et popularité.</li></ul>' },
            { Text: '<ul><li>Optimiser sa fiche Google My Business.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element3Arguments: [
            { Text: '<ul><li>Expérimenter les méthodes et outils d’audit de la partie technique de son site internet.</li></ul>' },
            { Text: '<ul><li>Étudier les sources d’optimisation les plus courantes.</li></ul>' },
            { Text: '<ul><li>Connaître les règles d’ergonomie.</li></ul>' },
            { Text: '<ul><li>Appliquer les techniques spécifiques pour le mobile.</li></ul>' },
            { Text: '<ul><li>Mesurer l’importance des balises.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element4Arguments: [
            { Text: '<ul><li>Identifier les mots-clés pertinents pour son activité.</li></ul>' },
            { Text: '<ul><li>Établir une stratégie de contenu en ligne avec des mots-clés ciblés (Outils : UberSuggest ou Semruch).</li></ul>' },
            { Text: '<ul><li>Connaître la technique de la longue traîne.</li></ul>' },
            { Text: '<ul><li>Appliquer les techniques rédactionnelles pour un contenu toujours plus optimisé (le cocon sémantique).</li></ul>' },
            { Text: '<ul><li>Tester les outils d’IA pour rédiger du contenu.</li></ul>' },
            { Text: '<ul><li>Optimiser ses images : structures, visuels, poids...</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element5Arguments: [
            { Text: '<ul><li>Comprendre les principes de popularité (domaine authority).</li></ul>' },
            { Text: '<ul><li>Construire une stratégie de netlinking (relations presse).</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element6Arguments: [
            { Text: '<ul><li>Maquetter ses balises.</li></ul>' },
            { Text: '<ul><li>Préparer un plan de redirections.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element7Arguments: [
            { Text: '<ul><li>Parcourir les fonctions avancées de Google Analytics.</li></ul>' },
            { Text: '<ul><li>Tester les outils d’analyse de positionnement SEO (type Semrush).</li></ul>' },
            { Text: '<ul><li>Identifier, mettre en place, lire et analyser les KPI clés d’acquisition de trafic.</li></ul>' },
            { Text: '<ul><li>Création d’un tableau de bord sur Google Data Studio.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>
        },
        // Data,
        $info: { prog01Seo: scrollInfo() },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      testProfilsExpertsClick () {
        openUrl('https://www.matchers.fr/expert/261', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      blocPreviewProfilExpertClick () {
        openUrl('https://www.matchers.fr/expert/241', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      element1Click () {
        openUrl('https://www.matchers.fr/expert/265', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      element2Click () {
        openUrl('https://www.matchers.fr/expert/482', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      voirClick () {
        openUrl('https://www.matchers.fr/experts', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return formationsSeoFactory(storeId)()
}

export const formationsSeoRaw = formationsSeoFactory()
export const formationsSeo = () => formationsSeoRaw() as MergeCustomStore<typeof formationsSeoStoreCustom.instance>
